import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Banknote,
  Calendar,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MessageCircle,
  Phone,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BeloLogo } from "@/components/BeloLogo";
import { Toaster } from "@/components/ui/sonner";
import { SoftphoneProvider } from "@/context/SoftphoneContext";
import GlobalSoftphone from "@/components/GlobalSoftphone";
import { api } from "@/lib/api";

const NAV = [
  { to: "/bdc", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "bdc-nav-dashboard" },
  { to: "/bdc/queue", label: "Lead Queue", icon: ListChecks, testId: "bdc-nav-queue" },
  { to: "/bdc/chat", label: "Chat", icon: MessageCircle, testId: "bdc-nav-chat" },
  { to: "/bdc/phone", label: "Phone", icon: Phone, testId: "bdc-nav-phone" },
  { to: "/bdc/appointments", label: "Appointments", icon: Calendar, testId: "bdc-nav-appointments" },
  { to: "/bdc/team", label: "Team", icon: Users, testId: "bdc-nav-team", badgeKey: "team_unread" },
  { to: "/bdc/settings/payment", label: "Payment", icon: Banknote, testId: "bdc-nav-payment" },
];

const BdcLayout = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [teamUnread, setTeamUnread] = useState(0);
  const location = useLocation();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  // Poll team unread every 20s so the Team nav badge stays fresh across
  // portal pages without users having to visit /bdc/team.
  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/bdc/team/channels");
      const total = data.reduce((n, c) => n + (c.unread_count || 0), 0);
      setTeamUnread(total);
    } catch {
      // silent — presence widget will surface issues if they persist
    }
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 20_000);
    return () => clearInterval(t);
  }, [loadUnread]);

  const badges = { team_unread: teamUnread };

  const initial = (user?.full_name || "?").charAt(0).toUpperCase();

  return (
    <SoftphoneProvider>
      <div className="min-h-screen bg-slate-100" data-testid="bdc-shell">
        {/* mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-slate-950 text-white">
          <div className="flex items-center justify-between px-4 h-14">
            <Link to="/bdc" className="flex items-center gap-2">
              <BeloLogo height={28} color="#ffffff" />
              <span className="text-[10px] tracking-[0.18em] uppercase text-white/70">BDC</span>
            </Link>
            <Button size="icon" variant="ghost" onClick={() => setOpen(true)} className="text-white" data-testid="bdc-mobile-menu-btn">
              <Menu size={22} />
            </Button>
          </div>
        </div>

        <aside
          className={`fixed lg:fixed top-0 left-0 z-40 h-screen w-64 shrink-0 bg-slate-950 text-slate-100 flex flex-col transform transition-transform lg:translate-x-0 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="bdc-sidebar"
        >
          <div className="px-5 h-16 flex items-center justify-between border-b border-white/5">
            <div className="flex items-center gap-2">
              <BeloLogo height={28} color="#ffffff" />
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-white/60">BDC</span>
            </div>
            <button className="lg:hidden text-white/70" onClick={() => setOpen(false)} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {NAV.map((item) => {
              const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-white/10 text-white shadow-inner"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <item.icon size={17} className="shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {badgeCount > 0 && (
                    <span className="rounded-full bg-brand text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center leading-4">
                      {badgeCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
            {user?.role === "bdc_manager" && (
              <>
                <div className="mt-4 mb-1 px-3 text-[10px] font-bold uppercase tracking-widest text-gold/80">
                  Manager
                </div>
                <NavLink
                  to="/bdc/manager/team"
                  data-testid="bdc-nav-manager-team"
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-gold/15 text-gold shadow-inner border border-gold/30"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <Users size={17} className="shrink-0" />
                  <span className="flex-1">Team overview</span>
                </NavLink>
              </>
            )}
          </nav>

          <div className="border-t border-white/5 p-3 space-y-2">
            <div className="rounded-lg bg-white/5 px-3 py-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold text-slate-900 font-semibold flex items-center justify-center text-sm">
                {initial}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.full_name}</p>
                <p className="text-[11px] text-white/60 truncate">{user?.role === "bdc_manager" ? "BDC Manager" : "BDC Agent"}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
              data-testid="bdc-logout-btn"
            >
              <LogOut size={16} /> Log out
            </Button>
          </div>
        </aside>

        {open && (
          <div className="fixed inset-0 z-30 bg-slate-900/60 lg:hidden" onClick={() => setOpen(false)} />
        )}

        <div className="lg:pl-64">
          <main className="min-h-screen" data-testid="bdc-main">
            <Outlet />
          </main>
        </div>
        <Toaster richColors position="top-right" />
        <GlobalSoftphone />
      </div>
    </SoftphoneProvider>
  );
};

export default BdcLayout;
